import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import {
  Receipt,
  TrendingUp,
  Wallet,
  Package,
  ShoppingCart,
  BarChart3,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../contexts/BranchContext';
import { subscribeToMonthlyPnL } from '../firebase/monthlyPnLService';

const Home = ({ permissions }) => {
  const navigate = useNavigate();
  const { activeBranch, loading: branchLoading } = useBranch();
  const [monthlyPnLRecord, setMonthlyPnLRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  // Profit/margin data is strictly admin-only — never gated by a togglable staff permission.
  const canViewProfitLoss = permissions?.userType === 'admin';

  const currentMonthId = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    if (!activeBranch) {
      setMonthlyPnLRecord(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const docId = `${activeBranch.id}_${currentMonthId}`;
    const unsub = subscribeToMonthlyPnL(docId, (data) => {
      setMonthlyPnLRecord(data);
      setLoading(false);
    });
    return () => unsub();
  }, [activeBranch, currentMonthId]);

  const StatCard = ({ icon, value, label, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: 'action.hover',
            color: 'text.primary',
            display: 'inline-flex',
            border: '1px solid',
            borderColor: 'divider',
            mb: 2,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const QuickLink = ({ icon, label, path }) => (
    <Grid item xs={6} sm={3}>
      <Button
        fullWidth
        variant="outlined"
        onClick={() => navigate(path)}
        sx={{ py: 2, flexDirection: 'column', gap: 1 }}
      >
        {icon}
        {label}
      </Button>
    </Grid>
  );

  if (loading || branchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const fmt = (n) => `LKR ${(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {activeBranch ? `${activeBranch.name} — this month's overview` : 'Business overview'}
        </Typography>
      </Box>

      {!activeBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No branch selected. Add a branch and select it from the header to see its Monthly P&L.
        </Alert>
      )}

      {activeBranch && !monthlyPnLRecord && canViewProfitLoss && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No Monthly P&L record has been saved for {activeBranch.name} this month yet.{' '}
          <Button size="small" onClick={() => navigate('/monthly-pnl')}>Fill it in now</Button>
        </Alert>
      )}

      {activeBranch && canViewProfitLoss && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Receipt size={24} />}
              value={fmt(monthlyPnLRecord?.revenue)}
              label="Revenue This Month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Package size={24} />}
              value={fmt(monthlyPnLRecord?.totalCOGS)}
              label="Cost of Goods Sold"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<Wallet size={24} />}
              value={fmt(monthlyPnLRecord?.grossProfit)}
              label="Gross Profit"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<TrendingUp size={24} />}
              value={fmt(monthlyPnLRecord?.netProfit)}
              label="Net Profit"
              subtitle="After expenses"
            />
          </Grid>
        </Grid>
      )}

      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Quick Links</Typography>
      <Grid container spacing={2}>
        <QuickLink icon={<ShoppingCart size={22} />} label="Products" path="/products" />
        <QuickLink icon={<Receipt size={22} />} label="Expenses" path="/expenses" />
        <QuickLink icon={<TrendingUp size={22} />} label="Monthly P&L" path="/monthly-pnl" />
        <QuickLink icon={<BarChart3 size={22} />} label="Reports" path="/reports" />
      </Grid>
    </Box>
  );
};

export default Home;
